import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  depotService, articleService, DepotView, StockDepotLigne, LotLigne, MouvementStockView,
} from '../../api/services';

/**
 * Multi-dépôt (TF-070) + lots/séries/DLC (TF-071) : référentiel des dépôts, transferts
 * inter-dépôts, stock par dépôt, solde par lot avec alertes de péremption et traçabilité.
 */
const inputCls = 'w-full rounded-lg border border-gray-200 px-3 py-2';

export function DepotsLots() {
  const [depots, setDepots] = useState<DepotView[]>([]);
  const [parDepot, setParDepot] = useState<StockDepotLigne[]>([]);
  const [lots, setLots] = useState<LotLigne[]>([]);
  const [articles, setArticles] = useState<any[]>([]);
  const [trace, setTrace] = useState<{ lot: string; mouvements: MouvementStockView[] } | null>(null);

  const [depotForm, setDepotForm] = useState({ code: '', nom: '', adresse: '' });
  const [transfert, setTransfert] = useState({
    articleId: '', depotSourceId: '', depotDestinationId: '', quantite: '', numeroLot: '',
  });
  const [busy, setBusy] = useState(false);

  const load = () => {
    depotService.list().then(setDepots).catch(() => {});
    depotService.stockParDepot().then(setParDepot).catch(() => {});
    depotService.lots().then(setLots).catch(() => {});
  };

  useEffect(() => {
    load();
    articleService.getAll({ size: 1000 }).then((p: any) => setArticles(p.content ?? p)).catch(() => {});
  }, []);

  const createDepot = async () => {
    if (!depotForm.code.trim() || !depotForm.nom.trim()) { toast.error('Code et nom requis.'); return; }
    setBusy(true);
    try {
      await depotService.create({ code: depotForm.code, nom: depotForm.nom, adresse: depotForm.adresse || undefined });
      setDepotForm({ code: '', nom: '', adresse: '' });
      toast.success('Dépôt créé.');
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Création impossible.');
    } finally { setBusy(false); }
  };

  const removeDepot = async (d: DepotView) => {
    if (!window.confirm(`Supprimer le dépôt ${d.nom} ? Ses mouvements retombent sur le dépôt principal.`)) return;
    try { await depotService.remove(d.id); toast.success('Dépôt supprimé.'); load(); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Suppression impossible.'); }
  };

  const lancerTransfert = async () => {
    if (!transfert.articleId || !transfert.quantite) { toast.error('Article et quantité requis.'); return; }
    setBusy(true);
    try {
      await depotService.transferer({
        articleId: Number(transfert.articleId),
        depotSourceId: transfert.depotSourceId === '' ? null : Number(transfert.depotSourceId),
        depotDestinationId: transfert.depotDestinationId === '' ? null : Number(transfert.depotDestinationId),
        quantite: Number(transfert.quantite),
        numeroLot: transfert.numeroLot || undefined,
      });
      toast.success('Transfert enregistré.');
      setTransfert({ articleId: '', depotSourceId: '', depotDestinationId: '', quantite: '', numeroLot: '' });
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Transfert impossible.');
    } finally { setBusy(false); }
  };

  const voirTrace = async (lot: string) => {
    try { setTrace({ lot, mouvements: await depotService.mouvementsLot(lot) }); }
    catch { toast.error('Traçabilité indisponible.'); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Dépôts & lots</h1>
        <p className="page-subtitle">Multi-dépôt, transferts inter-dépôts, lots / numéros de série / DLC</p>
      </div>

      {/* ----- Dépôts ----- */}
      <div className="card space-y-4">
        <h2 className="text-sm font-semibold text-gray-700">Dépôts</h2>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Code</label>
            <input value={depotForm.code} onChange={(e) => setDepotForm((f) => ({ ...f, code: e.target.value }))}
                   placeholder="DEP-02" className={inputCls + ' w-28'} />
          </div>
          <div className="flex-1 min-w-44">
            <label className="block text-xs text-gray-600 mb-1">Nom</label>
            <input value={depotForm.nom} onChange={(e) => setDepotForm((f) => ({ ...f, nom: e.target.value }))}
                   placeholder="Entrepôt Sfax" className={inputCls} />
          </div>
          <div className="flex-1 min-w-44">
            <label className="block text-xs text-gray-600 mb-1">Adresse (optionnel)</label>
            <input value={depotForm.adresse} onChange={(e) => setDepotForm((f) => ({ ...f, adresse: e.target.value }))}
                   className={inputCls} />
          </div>
          <button onClick={createDepot} disabled={busy} className="btn-primary">Créer</button>
        </div>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
            <tr><th className="px-4 py-2">Code</th><th className="px-4 py-2">Nom</th>
                <th className="px-4 py-2">Adresse</th><th className="px-4 py-2 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <tr><td className="px-4 py-2 font-mono text-xs">—</td>
                <td className="px-4 py-2 text-gray-500 italic">Dépôt principal (implicite)</td>
                <td className="px-4 py-2"></td><td></td></tr>
            {depots.map((d) => (
              <tr key={d.id}>
                <td className="px-4 py-2 font-mono text-xs">{d.code}</td>
                <td className="px-4 py-2">{d.nom}{!d.actif && <span className="ml-2 text-xs text-gray-400">(inactif)</span>}</td>
                <td className="px-4 py-2 text-gray-500">{d.adresse || '—'}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => removeDepot(d)} className="text-red-600 hover:underline">Supprimer</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ----- Transfert ----- */}
      <div className="card space-y-3">
        <h2 className="text-sm font-semibold text-gray-700">Transfert inter-dépôts</h2>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-52">
            <label className="block text-xs text-gray-600 mb-1">Article</label>
            <select value={transfert.articleId}
                    onChange={(e) => setTransfert((f) => ({ ...f, articleId: e.target.value }))} className={inputCls}>
              <option value="">— Choisir —</option>
              {articles.map((a: any) => <option key={a.id} value={a.id}>{a.reference} — {a.designation}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">De</label>
            <select value={transfert.depotSourceId}
                    onChange={(e) => setTransfert((f) => ({ ...f, depotSourceId: e.target.value }))} className={inputCls + ' w-44'}>
              <option value="">Dépôt principal</option>
              {depots.map((d) => <option key={d.id} value={d.id}>{d.nom}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Vers</label>
            <select value={transfert.depotDestinationId}
                    onChange={(e) => setTransfert((f) => ({ ...f, depotDestinationId: e.target.value }))} className={inputCls + ' w-44'}>
              <option value="">Dépôt principal</option>
              {depots.map((d) => <option key={d.id} value={d.id}>{d.nom}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Quantité</label>
            <input type="number" step="0.001" value={transfert.quantite}
                   onChange={(e) => setTransfert((f) => ({ ...f, quantite: e.target.value }))} className={inputCls + ' w-28'} />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Lot (optionnel)</label>
            <input value={transfert.numeroLot}
                   onChange={(e) => setTransfert((f) => ({ ...f, numeroLot: e.target.value }))} className={inputCls + ' w-32'} />
          </div>
          <button onClick={lancerTransfert} disabled={busy} className="btn-primary">Transférer</button>
        </div>
      </div>

      {/* ----- Stock par dépôt ----- */}
      <div className="card overflow-x-auto">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Stock par dépôt</h2>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
            <tr><th className="px-4 py-2">Article</th><th className="px-4 py-2">Dépôt</th>
                <th className="px-4 py-2 text-right">Quantité</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {parDepot.length === 0 && (
              <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-400">Aucun mouvement de stock.</td></tr>
            )}
            {parDepot.map((l, i) => (
              <tr key={i}>
                <td className="px-4 py-2">{l.reference} — {l.designation}</td>
                <td className="px-4 py-2">{l.depotNom}</td>
                <td className={`px-4 py-2 text-right font-medium ${l.quantite < 0 ? 'text-red-600' : ''}`}>{l.quantite}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ----- Lots & DLC ----- */}
      <div className="card overflow-x-auto">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">
          Lots & DLC <span className="text-xs font-normal text-gray-400">(alerte si DLC sous 30 jours)</span>
        </h2>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
            <tr><th className="px-4 py-2">Article</th><th className="px-4 py-2">Lot</th>
                <th className="px-4 py-2">DLC</th><th className="px-4 py-2 text-right">Solde</th>
                <th className="px-4 py-2 text-right">Traçabilité</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {lots.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                Aucun lot suivi — renseignez un numéro de lot sur vos mouvements d'entrée.
              </td></tr>
            )}
            {lots.map((l, i) => (
              <tr key={i} className={l.perime ? 'bg-red-50' : l.dlcProche ? 'bg-amber-50' : ''}>
                <td className="px-4 py-2">{l.reference} — {l.designation}</td>
                <td className="px-4 py-2 font-mono text-xs">{l.numeroLot}</td>
                <td className="px-4 py-2">
                  {l.dlc ? new Date(l.dlc).toLocaleDateString() : '—'}
                  {l.perime && <span className="ml-2 text-xs font-semibold text-red-700">Périmé</span>}
                  {l.dlcProche && <span className="ml-2 text-xs font-semibold text-amber-700">DLC proche</span>}
                </td>
                <td className="px-4 py-2 text-right font-medium">{l.solde}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => voirTrace(l.numeroLot)} className="text-blue-600 hover:underline">Tracer</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {trace && (
          <div className="mt-4 border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">Mouvements du lot <span className="font-mono">{trace.lot}</span></h3>
              <button onClick={() => setTrace(null)} className="text-xs text-gray-400 hover:underline">Fermer</button>
            </div>
            <table className="min-w-full text-sm mt-2">
              <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
                <tr><th className="px-4 py-2">Date</th><th className="px-4 py-2">Sens</th>
                    <th className="px-4 py-2">Dépôt</th><th className="px-4 py-2 text-right">Quantité</th>
                    <th className="px-4 py-2">Motif</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {trace.mouvements.map((m) => (
                  <tr key={m.id}>
                    <td className="px-4 py-2 text-xs text-gray-500">{new Date(m.createdAt).toLocaleString()}</td>
                    <td className="px-4 py-2">
                      <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${
                        m.sens === 'IN' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {m.sens === 'IN' ? 'Entrée' : 'Sortie'}
                      </span>
                    </td>
                    <td className="px-4 py-2">{m.depotNom || 'Dépôt principal'}</td>
                    <td className="px-4 py-2 text-right">{m.quantite}</td>
                    <td className="px-4 py-2 text-gray-500">{m.motif || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default DepotsLots;
